// Importaciones necesarias para el componente
import {
  Component,
  OnInit,
  EventEmitter,
  Output,
  HostListener,
  OnDestroy,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  RouterLink,
  RouterLinkActive,
  Router,
  NavigationEnd,
} from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { ToggleComponent } from '../toggle/toggle.component';
import { PreferencesService } from '../../service/components/preferences.service';
import { ExtModalComponent } from '../../components/ext-modal/ext-modal.component';
import { ThemeService } from '../../service/components/theme.service';
import { menuItems } from './sidebar.items';
import { NgbTooltipConfig, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { ButtonComponent } from '../button/button.component';
import { PermissionsService } from '../../service/token/permissions.service';

// Interfaz para los subitems del menú
export interface SubMenuItem {
  name: string;
  icon?: string;
  route?: string;
}

// Interfaz para los items principales del menú
export interface MenuItem {
  name: string;
  icon: string;
  route?: string;
  subItems?: SubMenuItem[];
  hasActiveSubitem?: boolean;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    NgbTooltipModule,
    RouterLinkActive,
    ToggleComponent,
    ExtModalComponent,
    ButtonComponent,
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css',
})
export class SidebarComponent implements OnInit, OnDestroy {
  username: string = "Christina's";
  expandedItemIndex: number | null = null;
  currentUrl: string = '';

  // Indica si el sidebar está en modo colapsado (solo iconos)
  isCollapsed = false;

  // Indica si el sidebar está temporalmente expandido en vista móvil
  isExpanded = false;

  // Ancho de la ventana actual
  windowWidth = window.innerWidth;

  // Indica si se está usando el modo oscuro
  isDarkMode = false;

  // Variable que controla el estado real del toggle (valor interno)
  // Esta es la que está enlazada al componente toggle
  isTableView = false;

  // Variable que controla el estado real de la preferencia (valor persistido)
  // Esta es la que se actualiza cuando se confirma el cambio
  private actualTableViewPreference = false;

  // Suscripciones para liberarlas cuando se destruya el componente
  private subscriptions: Subscription[] = [];

  // Modal de confirmación para el modo tabla
  showTableModeModal: boolean = false;
  pendingTableViewValue: boolean = false;

  // Items del menú originales
  private allMenuItems = menuItems;

  // Items del menú filtrados por permisos
  menuItems: MenuItem[] = [];

  @Output() logoutRequest = new EventEmitter<void>();
  // Emite cuando el estado del sidebar cambia (expandido o colapsado)
  @Output() sidebarToggled = new EventEmitter<boolean>();

  // Referencia al componente toggle para poder actualizarlo directamente
  @ViewChild('tableViewToggle') tableViewToggle!: ToggleComponent;

  constructor(
    private router: Router,
    private preferencesService: PreferencesService,
    private cdr: ChangeDetectorRef,
    private themeService: ThemeService,
    public tooltipConfig: NgbTooltipConfig,
    private permissionsService: PermissionsService
  ) {
    this.currentUrl = this.router.url;
    this.checkScreenSize();

    // Configuración global de tooltips
    this.tooltipConfig.autoClose = true;
    this.tooltipConfig.triggers = 'hover';
    this.tooltipConfig.openDelay = 0;
    this.tooltipConfig.closeDelay = 0;
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.windowWidth = window.innerWidth;
    this.checkScreenSize();
  }

  // Verifica el tamaño de la pantalla y ajusta el estado del sidebar
  checkScreenSize(): void {
    this.isCollapsed = window.innerWidth <= 1040;

    // Si cambiamos a una pantalla grande, reseteamos el estado expandido
    if (!this.isCollapsed) {
      this.isExpanded = false;
    }
  }

  // Alterna el estado expandido del sidebar en modo móvil
  toggleSidebar(): void {
    if (this.isCollapsed) {
      this.isExpanded = !this.isExpanded;

      // Emitir el evento con el nuevo estado
      this.sidebarToggled.emit(this.isExpanded);

      // Si cerramos el sidebar, también cerramos cualquier submenú abierto
      if (!this.isExpanded) {
        this.expandedItemIndex = null;
      }
    }
  }

  ngOnInit(): void {
    // Filtrar items del menú según permisos
    this.filterMenuItemsByPermissions();

    // Suscribirse a cambios en los permisos
    const permissionsSub = this.permissionsService.userPermissions$.subscribe(
      () => {
        this.filterMenuItemsByPermissions();
      }
    );
    this.subscriptions.push(permissionsSub);

    // Suscribirse al estado del tema
    const themeSub = this.themeService.darkMode$.subscribe((isDark) => {
      this.isDarkMode = isDark;
    });
    this.subscriptions.push(themeSub);

    // Sincronizar el estado del toggle al iniciar
    this.syncToggleState();

    // Detectar cambios en la ruta para mantener el estado del menú
    const routerSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.currentUrl = this.router.url;
        this.checkActiveRoute();

        // En móvil, después de navegar cerramos el sidebar y los submenús
        if (this.isCollapsed) {
          this.isExpanded = false;
          this.expandedItemIndex = null;
        }
      });
    this.subscriptions.push(routerSub);

    // Inicializar al cargar la página
    this.checkActiveRoute();
    this.checkScreenSize();
  }

  // Limpia las suscripciones al destruir el componente
  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  // Verifica si un subitem está activo
  isSubitemActive(subItem: SubMenuItem): boolean {
    if (!subItem.route) return false;

    // Obtener la URL actual sin parámetros
    const currentPath = this.router.url.split('?')[0];
    const subItemRoute = subItem.route.split('?')[0];

    // Verificar si la URL actual coincide exactamente con la ruta
    const exactMatch = currentPath === subItemRoute;

    // Verificar si la URL actual comienza con la ruta del subitem
    const startsWith = currentPath.startsWith(subItemRoute);

    // Usar isActive de Router para verificar rutas anidadas
    const isRouterActive = this.router.isActive(subItemRoute, {
      paths: 'subset',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });

    return exactMatch || startsWith || isRouterActive;
  }

  // Revisa si alguna ruta en los subitems coincide con la URL actual y expande el menú correspondiente
  checkActiveRoute(): void {
    // Resetear el estado de items con subitems activos
    this.menuItems.forEach((item) => {
      item.hasActiveSubitem = false;
    });

    let foundActiveSubmenu = false;

    // Verificar cada item del menú
    for (let i = 0; i < this.menuItems.length; i++) {
      const item = this.menuItems[i];

      // Si no tiene subitems, ignoramos
      if (!this.hasSubItems(item)) continue;

      // Verificar si alguno de los subitems tiene la ruta activa
      const hasActiveSubItem = item.subItems?.some(
        (subItem) => subItem.route && this.currentUrl.includes(subItem.route)
      );

      // Marcar el ítem padre si tiene un subitem activo
      if (hasActiveSubItem) {
        item.hasActiveSubitem = true;
        foundActiveSubmenu = true;

        // Si no estamos en vista móvil, expandimos el menú
        if (!this.isCollapsed) {
          this.expandedItemIndex = i;
        }
      }
    }

    // Si no se encontró ningún submenú activo o estamos en vista móvil, cerrar todos
    if (!foundActiveSubmenu || this.isCollapsed) {
      this.expandedItemIndex = null;
    }
  }

  // Expande o contrae un item del menú
  toggleSubMenu(index: number): void {
    // Si es el mismo ítem que ya está expandido, lo cerramos
    if (this.expandedItemIndex === index) {
      this.expandedItemIndex = null;
    } else {
      // Si es un ítem diferente, cerramos el anterior y abrimos este
      this.expandedItemIndex = index;
    }
  }

  // Navega a una ruta y cierra los menús solo en móvil
  navigateAndClose(
    route: string | undefined,
    isSubitem: boolean = false
  ): void {
    if (route) {
      this.router.navigate([route]);

      // En móvil siempre cerramos el sidebar
      if (this.isCollapsed) {
        this.isExpanded = false;

        // Si hacemos clic en un ítem principal (no en un subitem), cerramos los submenús
        if (!isSubitem) {
          this.expandedItemIndex = null;
        }
      } else {
        // En escritorio, solo cerramos el submenú si hacemos clic en un ítem principal
        if (!isSubitem) {
          this.expandedItemIndex = null;
        }
      }

      // Después de navegar, asegurarnos de marcar los ítems activos
      setTimeout(() => {
        this.checkActiveRoute();
      }, 100);
    }
  }

  // Verifica si un item del menú tiene subitems
  hasSubItems(item: MenuItem): boolean {
    return !!item.subItems && item.subItems.length > 0;
  }

  onLogoutClick(): void {
    this.logoutRequest.emit();
  }

  // Maneja el cambio en el toggle de vista de tabla/cards
  handleTableViewChange(newValue: boolean): void {
    // Si se está activando el modo tabla, mostrar el modal de confirmación
    if (newValue && !this.actualTableViewPreference) {
      // El usuario intenta activar el modo tabla, mostrar la confirmación
      this.pendingTableViewValue = true;
      this.showTableModeModal = true;

      // Revertir inmediatamente el valor a su estado original hasta que se confirme
      setTimeout(() => {
        this.isTableView = this.actualTableViewPreference;
        this.cdr.detectChanges();
      }, 0);
    } else if (!newValue && this.actualTableViewPreference) {
      // El usuario está desactivando el modo tabla, aplicar directamente
      this.actualTableViewPreference = false;
      this.preferencesService.setTableView(false);

      // Actualizar el estado visual con retraso
      setTimeout(() => {
        this.isTableView = false;
        this.cdr.detectChanges();
      }, 50);
    }
  }

  // Confirma el cambio al modo tabla
  confirmTableMode(): void {
    // Actualizar estado interno y preferencia real
    this.actualTableViewPreference = true;
    this.preferencesService.setTableView(true);

    // Actualizar interfaz de usuario con un retraso para permitir la sincronización
    setTimeout(() => {
      this.isTableView = true;
      this.cdr.detectChanges();
      this.updateToggle();
    }, 50);

    this.showTableModeModal = false;
  }

  // Cancela el cambio al modo tabla
  cancelTableMode(): void {
    this.showTableModeModal = false;

    // Asegurarse de que el toggle vuelva a su estado original con un retraso
    setTimeout(() => {
      this.isTableView = this.actualTableViewPreference;
      this.cdr.detectChanges();
      this.updateToggle();
    }, 50);
  }

  // Actualiza explícitamente el componente toggle
  private updateToggle(): void {
    if (this.tableViewToggle) {
      this.tableViewToggle.writeValue(this.isTableView);
      this.tableViewToggle.checked = this.isTableView;
      this.cdr.detectChanges();
    }
  }

  // Actualiza explícitamente el estado del toggle basado en las preferencias actuales
  private syncToggleState(): void {
    // Obtener el estado actual una sola vez
    this.preferencesService.getPreferences().subscribe((prefs) => {
      this.isTableView = prefs.useTableView;
      this.actualTableViewPreference = prefs.useTableView;
      this.cdr.detectChanges();
    });

    // Suscribirse a futuros cambios
    const prefsSub = this.preferencesService
      .getPreferences()
      .subscribe((prefs) => {
        // Solo actualizar si el estado actual es diferente
        if (this.actualTableViewPreference !== prefs.useTableView) {
          this.actualTableViewPreference = prefs.useTableView;
          this.isTableView = prefs.useTableView;
          this.cdr.detectChanges();
        }
      });
    this.subscriptions.push(prefsSub);
  }

  /**
   * Alterna entre el modo oscuro y claro.
   */
  toggleDarkMode(): void {
    this.themeService.toggleDarkMode();
  }

  /**
   * Filtra los items del menú según los permisos de lectura del usuario
   * Si no tiene permiso de lectura, no se muestra la opción en el sidebar
   */
  private filterMenuItemsByPermissions(): void {
    this.menuItems = this.allMenuItems
      .map((item) => {
        // Si el item tiene una ruta directa, verificar permiso de lectura
        if (item.route) {
          const section = this.permissionsService.getRouteSection(item.route);
          if (section) {
            // Verificar específicamente permiso de lectura
            if (!this.permissionsService.hasSectionAction(section, 'read')) {
              return null; // No mostrar si no tiene permiso de lectura
            }
          }
          // Si no tiene sección mapeada, permitir (ej: /home, /home/settings)
          return item;
        }

        // Si el item tiene subitems, filtrarlos
        if (item.subItems && item.subItems.length > 0) {
          const filteredSubItems = item.subItems.filter((subItem) => {
            if (!subItem.route) return true;
            const section = this.permissionsService.getRouteSection(
              subItem.route
            );
            if (!section) return true; // Si no hay mapeo, permitir
            // Verificar específicamente permiso de lectura
            return this.permissionsService.hasSectionAction(section, 'read');
          });

          // Si no quedan subitems, no mostrar el item padre
          if (filteredSubItems.length === 0) {
            return null;
          }

          // Retornar el item con los subitems filtrados
          return {
            ...item,
            subItems: filteredSubItems,
          };
        }

        // Si no tiene ruta ni subitems, permitir
        return item;
      })
      .filter((item): item is MenuItem => item !== null);
  }
}
